// Phase 2 PR1 of the adaptive-lookup-field refactor. See
// `docs/audits/adaptive-field-refactor-proposal.md` §3.1.
//
// One component that replaces both `LookupFieldSelect` (single-value) and
// `LookupMultiSelect` (multi-value) by consulting the collection doc's own
// `isMultiSelect` / `supportsSynonyms` / `supportsGrouping` flags at render
// time. `LookupFieldSelect` and `LookupMultiSelect` are now thin compat shims
// that delegate here, so all existing call sites keep their current API.
//
// Deliberate divergences from the RN `CombinedLookupField`:
//   - Native <select> preserved for single-mode (density matters on a 47-field
//     ref editor form; a modal picker per row would be brutal).
//   - `supportsMeasurement` is READ but NOT rendered — admin is a curator
//     tool, always shows canonical displayName. Kept in the code path so we
//     don't have to replumb when RN parity becomes a Phase-4 concern.
//
// Storage props are split rather than a discriminated union: each call site
// keeps its existing `useState<string>` / `useState<string[]>` unchanged
// during migration. When a field flips single→multi in Phase 3 the call site
// swaps which prop pair it passes; useState type changes locally. Auto-migrating
// via codemod is a bad idea — the useState shape leaks into diff/serialize code
// paths that need eyes on them.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLookupCollectionFull } from '../../services/firebase';
import type { LookupValue } from '../../types';
import { CustomValueActions } from './CustomValueActions';

export interface AdaptiveLookupFieldProps {
  label: string;
  lookupCollection: string;

  // Storage bridge — pass ONE pair based on the field's current storage
  // shape. AdaptiveLookupField uses whichever branch matches the loaded
  // collection's `isMultiSelect` flag, and warns in dev if the caller passed
  // the wrong pair (shape mismatch with the collection doc).
  singleValue?: string;
  onSingleChange?: (value: string) => void;
  multiValues?: string[];
  onMultiChange?: (values: string[]) => void;

  disabled?: boolean;
  placeholder?: string;

  // `allowCustom={false}` is Brand Portal's "read-only lookup" path — hides
  // create-new / synonym affordances, optionally exposes a "Request new value"
  // button that the parent wires to its own request modal.
  allowCustom?: boolean;
  onRequestNewValue?: (seed: { query?: string }) => void;
  requestSent?: boolean;
}

// Case-insensitive trim compare — shared by both branches.
const eq = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Does this stored slug match `lv`? `supportsSynonyms` gates synonym-aware
 * matching so a collection with the flag off doesn't quietly coerce user
 * input against stale/legacy synonyms.
 */
function matchesLookup(
  stored: string,
  lv: LookupValue,
  supportsSynonyms: boolean,
): boolean {
  if (eq(stored, lv.value)) return true;
  if (eq(stored, lv.displayName)) return true;
  if (supportsSynonyms && lv.synonyms?.some((s) => eq(stored, s))) return true;
  return false;
}

/**
 * Group active lookup values by their `grouping` field. Preserves each
 * value's existing sort order within its group; groups themselves are ordered
 * by first appearance. Values without `grouping` land in a synthetic
 * ungrouped bucket rendered last. Matches RN's dynamic-grouping behaviour
 * (`CombinedLookupField.tsx:97`) so we don't need a per-collection config
 * flag flip to start rendering headers — as soon as any value carries
 * `grouping`, headers appear.
 */
function groupValues(
  values: LookupValue[],
): { group: string | null; values: LookupValue[] }[] {
  const groups = new Map<string | null, LookupValue[]>();
  const order: (string | null)[] = [];
  for (const v of values) {
    const key = v.grouping?.trim() || null;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(v);
  }
  return order.map((key) => ({ group: key, values: groups.get(key)! }));
}

export function AdaptiveLookupField(props: AdaptiveLookupFieldProps) {
  const {
    label,
    lookupCollection,
    singleValue,
    onSingleChange,
    multiValues,
    onMultiChange,
    disabled = false,
    placeholder = 'Select...',
    allowCustom = true,
    onRequestNewValue,
    requestSent = false,
  } = props;

  const [lookupValues, setLookupValues] = useState<LookupValue[]>([]);
  const [isMultiSelect, setIsMultiSelect] = useState<boolean>(false);
  const [supportsSynonyms, setSupportsSynonyms] = useState<boolean>(true);
  const [supportsGrouping, setSupportsGrouping] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  const loadCollection = useCallback(async () => {
    setLoading(true);
    try {
      // `getLookupCollectionFull` is session-cached and de-dupes concurrent
      // callers, so this is NOT a per-field Firestore read: the ~48 lookup
      // fields on a ref editor, the editor's own movement prefetch and
      // CustomCalibreModal all converge on one document read per collection.
      // Cache is dropped whenever a lookup is edited, so `onRefresh` after a
      // create-new-value still sees the new value.
      const full = await getLookupCollectionFull(lookupCollection);
      if (full) {
        setLookupValues(full.values.filter((v) => v.isActive));
        setIsMultiSelect(!!full.isMultiSelect);
        setSupportsSynonyms(!!full.supportsSynonyms);
        // Match RN: render group headers whenever the collection carries the
        // flag OR any value ships with a `grouping` string. This lets a
        // curator start grouping by adding `grouping` to values without
        // needing a schema-flag flip.
        setSupportsGrouping(
          !!full.supportsGrouping ||
            full.values.some((v) => !!v.grouping?.trim()),
        );
      } else {
        setLookupValues([]);
      }
    } catch (err) {
      // Match legacy behaviour — swallow + log; the picker just shows empty.
      console.error('AdaptiveLookupField: load failed for', lookupCollection, err);
      setLookupValues([]);
    } finally {
      setLoading(false);
    }
  }, [lookupCollection]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  // The collection's `isMultiSelect` flag normally decides which branch
  // renders. When it disagrees with what the caller actually bound, the
  // BINDING wins.
  //
  // PR1 deliberately let the flag win and rendered the mismatched branch
  // unbound, as a "config drift signal". In practice that signal is a field
  // that looks fine, shows nothing selected, and silently discards every edit
  // — indistinguishable from a broken screen, and only visible to whoever has
  // the console open. Marker Type and Numerals sat on exactly that fault line
  // until the marker-type split made both fields multi (Dial Color is still
  // the other way round: multi binding, single-flagged collection). Honouring
  // the binding keeps such a field usable no matter which way the Firestore
  // doc is set, and the dev warning below still names the drift.
  //
  // Only a genuine one-sided mismatch overrides the flag. A caller that bound
  // both pairs, or neither, leaves the flag in charge — that's the Phase 3
  // flip path, where the collection doc is the intended source of truth.
  const boundSingle = onSingleChange !== undefined;
  const boundMulti = onMultiChange !== undefined;
  const effectiveMultiSelect = isMultiSelect
    ? !(boundSingle && !boundMulti)
    : boundMulti && !boundSingle;

  if (import.meta.env?.DEV && !loading && effectiveMultiSelect !== isMultiSelect) {
    console.warn(
      `AdaptiveLookupField(${lookupCollection}): collection doc says ` +
        `isMultiSelect=${isMultiSelect} but the call site bound the ` +
        `${boundMulti ? 'multi' : 'single'} pair. Rendering the bound shape. ` +
        `Fix the collection's isMultiSelect flag or the call site.`,
    );
  }

  if (loading) {
    return (
      <div className="min-w-0">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  if (effectiveMultiSelect) {
    return (
      <MultiSelectBranch
        label={label}
        lookupCollection={lookupCollection}
        values={multiValues ?? []}
        onChange={onMultiChange ?? (() => {})}
        disabled={disabled}
        allowCustom={allowCustom}
        onRequestNewValue={onRequestNewValue}
        requestSent={requestSent}
        lookupValues={lookupValues}
        supportsSynonyms={supportsSynonyms}
        supportsGrouping={supportsGrouping}
        onRefresh={loadCollection}
      />
    );
  }

  return (
    <SingleSelectBranch
      label={label}
      lookupCollection={lookupCollection}
      value={singleValue}
      onChange={onSingleChange ?? (() => {})}
      disabled={disabled}
      placeholder={placeholder}
      allowCustom={allowCustom}
      onRequestNewValue={onRequestNewValue}
      requestSent={requestSent}
      lookupValues={lookupValues}
      supportsSynonyms={supportsSynonyms}
      supportsGrouping={supportsGrouping}
      onRefresh={loadCollection}
    />
  );
}

// ---------------------------------------------------------------------------
// Single-select branch — native <select> preserved (see file-top rationale).
// Behaviour and DOM shape are a 1:1 port of the pre-adaptive LookupFieldSelect
// so the migration doesn't regress the ref editor's dense form layout.
// ---------------------------------------------------------------------------

interface SingleBranchProps {
  label: string;
  lookupCollection: string;
  value: string | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder: string;
  allowCustom: boolean;
  onRequestNewValue?: (seed: { query?: string }) => void;
  requestSent: boolean;
  lookupValues: LookupValue[];
  supportsSynonyms: boolean;
  supportsGrouping: boolean;
  onRefresh: () => void;
}

function SingleSelectBranch({
  label,
  lookupCollection,
  value,
  onChange,
  disabled,
  placeholder,
  allowCustom,
  onRequestNewValue,
  requestSent,
  lookupValues,
  supportsSynonyms,
  supportsGrouping,
  onRefresh,
}: SingleBranchProps) {
  const [useCustom, setUseCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const matchingLookup = useMemo(() => {
    if (!value) return null;
    return (
      lookupValues.find((lv) => matchesLookup(value, lv, supportsSynonyms)) ??
      null
    );
  }, [value, lookupValues, supportsSynonyms]);

  // Normalize the select's value to the canonical slug when a match exists,
  // else pass through the raw stored value (custom-value case).
  const selectValue = useMemo(() => {
    if (matchingLookup) return matchingLookup.value;
    return value || '';
  }, [matchingLookup, value]);

  const hasCustomValue = useMemo(
    () => !!value && !matchingLookup && lookupValues.length > 0,
    [value, matchingLookup, lookupValues],
  );

  // If the loaded values arrive AFTER the parent set `value`, flip into
  // custom-input mode when nothing matches. Legacy LookupFieldSelect did the
  // same on `loading` transition — we react on `lookupValues.length` since
  // this component's loading is owned by the parent AdaptiveLookupField.
  useEffect(() => {
    if (!value || lookupValues.length === 0) return;
    const found = lookupValues.find((lv) =>
      matchesLookup(value, lv, supportsSynonyms),
    );
    if (!found) {
      setUseCustom(true);
      setCustomValue(value);
    } else {
      setUseCustom(false);
      setCustomValue('');
    }
  }, [value, lookupValues, supportsSynonyms]);

  const grouped = useMemo(
    () => (supportsGrouping ? groupValues(lookupValues) : null),
    [supportsGrouping, lookupValues],
  );

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected === '__custom__') {
      setUseCustom(true);
      setCustomValue(value || '');
    } else {
      setUseCustom(false);
      setCustomValue('');
      onChange(selected);
    }
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCustomValue(newValue);
    onChange(newValue);
  };

  const handleClearCustom = () => {
    setUseCustom(false);
    setCustomValue('');
    onChange('');
  };

  const renderOptions = () => {
    if (grouped) {
      return grouped.map(({ group, values: gv }) => {
        const opts = gv.map((lv) => (
          <option key={lv.id} value={lv.value}>
            {lv.displayName || lv.value}
          </option>
        ));
        if (group === null) {
          // Ungrouped tail — render as bare options, no header.
          return <optgroup key="__ungrouped__" label="Other">{opts}</optgroup>;
        }
        return (
          <optgroup key={group} label={group}>
            {opts}
          </optgroup>
        );
      });
    }
    return lookupValues.map((lv) => (
      <option key={lv.id} value={lv.value}>
        {lv.displayName || lv.value}
      </option>
    ));
  };

  return (
    <div className="min-w-0">
      {(label || (hasCustomValue && !useCustom)) && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {hasCustomValue && !useCustom && (
            <span className="ml-2 text-xs text-amber-600">(custom value)</span>
          )}
        </label>
      )}
      {allowCustom && useCustom ? (
        <div className="space-y-1">
          <div className="flex gap-2 items-center min-w-0">
            <input
              type="text"
              value={customValue}
              onChange={handleCustomChange}
              placeholder="Enter custom value..."
              disabled={disabled}
              className="flex-1 min-w-0 border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100"
            />
            {customValue.trim() && (
              <CustomValueActions
                customValue={customValue.trim()}
                lookupCollection={lookupCollection}
                onValueAdopted={(v) => {
                  setUseCustom(false);
                  setCustomValue('');
                  onChange(v);
                }}
                onRefresh={onRefresh}
                disabled={disabled}
              />
            )}
          </div>
          <button
            type="button"
            onClick={handleClearCustom}
            disabled={disabled}
            className="text-xs text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
          >
            Clear and show dropdown
          </button>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="flex gap-2 items-center min-w-0">
            <select
              value={selectValue}
              onChange={handleSelectChange}
              disabled={disabled}
              className={`flex-1 min-w-0 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100 ${
                hasCustomValue ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
              }`}
            >
              <option value="">{placeholder}</option>
              {hasCustomValue && (
                <option value={value} className="text-amber-700">
                  {value} (custom)
                </option>
              )}
              {renderOptions()}
              {allowCustom && (
                <option value="__custom__">-- Enter custom value --</option>
              )}
            </select>
            {allowCustom && hasCustomValue && value && (
              <CustomValueActions
                customValue={value}
                lookupCollection={lookupCollection}
                onValueAdopted={(v) => onChange(v)}
                onRefresh={onRefresh}
                disabled={disabled}
              />
            )}
          </div>
          {allowCustom && hasCustomValue && (
            <button
              type="button"
              onClick={handleClearCustom}
              disabled={disabled}
              className="text-xs text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
            >
              Clear custom value
            </button>
          )}
          {!allowCustom &&
            (onRequestNewValue ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onRequestNewValue({ query: value || undefined })}
                  disabled={disabled}
                  className="text-xs text-brand-600 hover:text-brand-700 underline disabled:opacity-50"
                >
                  + Request new value
                </button>
                {requestSent && (
                  <span className="text-xs text-green-600">✓ Request sent</span>
                )}
              </div>
            ) : (
              <button
                type="button"
                aria-disabled="true"
                title="Don't see yours? Contact support to add it"
                className="text-xs text-gray-400 cursor-not-allowed"
              >
                Request new value →
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-select branch — 1:1 port of the pre-adaptive LookupMultiSelect DOM,
// gated on the same collection flags as the single branch. Group headers are
// rendered by inserting a bold row before each group's checkbox tile grid;
// stored values remain a flat `string[]`.
// ---------------------------------------------------------------------------

interface MultiBranchProps {
  label: string;
  lookupCollection: string;
  values: string[];
  onChange: (values: string[]) => void;
  disabled: boolean;
  allowCustom: boolean;
  onRequestNewValue?: (seed: { query?: string }) => void;
  requestSent: boolean;
  lookupValues: LookupValue[];
  supportsSynonyms: boolean;
  supportsGrouping: boolean;
  onRefresh: () => void;
}

function MultiSelectBranch({
  label,
  lookupCollection,
  values,
  onChange,
  disabled,
  allowCustom,
  onRequestNewValue,
  requestSent,
  lookupValues,
  supportsSynonyms,
  supportsGrouping,
  onRefresh,
}: MultiBranchProps) {
  const [customInput, setCustomInput] = useState('');

  const isSelected = (lv: LookupValue) =>
    values.some((v) => matchesLookup(v, lv, supportsSynonyms));

  const handleToggle = (lv: LookupValue) => {
    const matchingStored = values.find((v) =>
      matchesLookup(v, lv, supportsSynonyms),
    );
    if (matchingStored) {
      onChange(values.filter((v) => v !== matchingStored));
    } else {
      onChange([...values, lv.value]);
    }
  };

  const handleAddCustom = () => {
    const trimmed = customInput.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setCustomInput('');
    }
  };

  const handleRemove = (v: string) => {
    onChange(values.filter((x) => x !== v));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  const findMatching = (v: string): LookupValue | undefined =>
    lookupValues.find((lv) => matchesLookup(v, lv, supportsSynonyms));

  const getDisplayName = (v: string) => findMatching(v)?.displayName || v;
  const isCustomValue = (v: string) => !findMatching(v);

  const grouped = useMemo(
    () => (supportsGrouping ? groupValues(lookupValues) : null),
    [supportsGrouping, lookupValues],
  );

  const renderCheckbox = (lv: LookupValue) => (
    <label
      key={lv.id}
      className={`flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-gray-50 ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={isSelected(lv)}
        onChange={() => handleToggle(lv)}
        disabled={disabled}
        className="rounded text-brand-600 focus:ring-brand-500"
      />
      <span className="text-sm text-gray-700 truncate">
        {lv.displayName || lv.value}
      </span>
    </label>
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {/* Selected values */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {values.map((v) => (
            <span
              key={v}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                isCustomValue(v)
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-brand-100 text-brand-800'
              }`}
            >
              {getDisplayName(v)}
              {allowCustom && isCustomValue(v) && (
                <CustomValueActions
                  customValue={v}
                  lookupCollection={lookupCollection}
                  onValueAdopted={(newValue) => {
                    const replaced = values.map((x) =>
                      x === v ? newValue : x,
                    );
                    const deduped: string[] = [];
                    for (const x of replaced) {
                      if (!deduped.includes(x)) deduped.push(x);
                    }
                    onChange(deduped);
                  }}
                  onRefresh={onRefresh}
                  disabled={disabled}
                />
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(v)}
                  className="hover:text-red-600"
                >
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Lookup options */}
      <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto mb-3">
        {grouped ? (
          <div className="space-y-3">
            {grouped.map(({ group, values: gv }) => (
              <div key={group ?? '__ungrouped__'}>
                {group && (
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {group}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {gv.map(renderCheckbox)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {lookupValues.map(renderCheckbox)}
          </div>
        )}
      </div>

      {/* Custom value input */}
      {allowCustom && !disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add custom value..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <button
            type="button"
            onClick={handleAddCustom}
            disabled={!customInput.trim()}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      )}

      {/* Request-new-value affordance (Brand Portal Phase 2.5 request flow). */}
      {!allowCustom &&
        (onRequestNewValue ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onRequestNewValue({ query: undefined })}
              disabled={disabled}
              className="text-xs text-brand-600 hover:text-brand-700 underline disabled:opacity-50"
            >
              + Request new value
            </button>
            {requestSent && (
              <span className="text-xs text-green-600">✓ Request sent</span>
            )}
          </div>
        ) : (
          <button
            type="button"
            aria-disabled="true"
            title="Don't see yours? Contact support to add it"
            className="text-xs text-gray-400 cursor-not-allowed"
          >
            Request new value →
          </button>
        ))}
    </div>
  );
}
